/**
 * Cómo se le cuenta a una persona que el servidor rechazó un formulario.
 *
 * ⚠ **«Datos de entrada inválidos» no es un mensaje, es un acertijo.** El servidor
 * sí dice qué campo falló y por qué —§5 lo manda en `details.campos`— y hasta ahora
 * la pantalla enseñaba sólo el titular y tiraba el resto. Con siete campos en el
 * formulario, eso deja a quien lo rellenó buscando a ojo cuál de los siete está mal.
 *
 * ⚠ **Los nombres y los mensajes se traducen aquí y no en el servidor.** El nombre
 * que viaja es el del esquema (`vence_en`), que es lo correcto para una API; el que
 * se lee es el de la etiqueta del campo. Y el texto de Pydantic viene en inglés: se
 * traducen los cuatro casos que salen a diario y el resto pasa tal cual, que es peor
 * que traducido pero mucho mejor que nada.
 */

/** Del nombre del esquema al de la etiqueta que la persona tiene delante. */
const NOMBRE: Record<string, string> = {
  titulo: "Título",
  descripcion: "Descripción",
  tipo: "Tipo",
  prioridad: "Prioridad",
  estado: "Estado",
  vence_en: "Vence el",
  asignado_a: "Asignar a",
  motivo: "Motivo del cambio",
  nota: "Nota",
  porcentaje: "Porcentaje",
  cuerpo: "Mensaje",
  email: "Correo",
  password: "Contraseña",
  monto: "Monto",
  fecha: "Fecha",
  cliente_id: "Cliente",
  proveedor_id: "Proveedor",
  aduana_id: "Aduana",
  cuenta_id: "Cuenta",
}

/** Los mensajes de Pydantic que salen a diario. El resto viaja tal cual. */
const TRADUCCION: [RegExp, string][] = [
  [/^Field required$/i, "hace falta"],
  [/valid date/i, "no es una fecha válida"],
  [/valid integer|valid number/i, "tiene que ser un número"],
  [/at least (\d+) character/i, "es demasiado corto"],
  [/at most (\d+) character/i, "es demasiado largo"],
  [/greater than or equal/i, "se queda por debajo del mínimo"],
  [/less than or equal/i, "pasa del máximo"],
]

function traducir(mensaje: string): string {
  for (const [patron, castellano] of TRADUCCION) {
    if (patron.test(mensaje)) return castellano
  }
  return mensaje
}

export function nombreDeCampo(campo: string): string {
  // Sólo la última parte: el servidor manda `items.0.monto` para una lista.
  const hoja = campo.split(".").pop() ?? campo
  return NOMBRE[hoja] ?? hoja
}

type CampoInvalido = { campo?: unknown; mensaje?: unknown }

/**
 * «Vence el: no es una fecha válida», a partir de `details.campos`.
 *
 * Devuelve `null` cuando no hay nada que resumir, para que quien llame se quede con
 * el mensaje original en vez de enseñar una cadena vacía.
 */
export function resumirValidacion(detalles: Record<string, unknown> | null): string | null {
  const campos = detalles?.campos
  if (!Array.isArray(campos) || campos.length === 0) return null
  const partes = (campos as CampoInvalido[])
    .map((c) => {
      if (typeof c.campo !== "string") return null
      const mensaje = typeof c.mensaje === "string" ? traducir(c.mensaje) : "no es válido"
      return `${nombreDeCampo(c.campo)}: ${mensaje}`
    })
    .filter((p): p is string => p !== null)
  return partes.length === 0 ? null : partes.join(" · ")
}
